import React, { useState, useEffect, useRef } from 'react';
import { Mail, Plus, Send, Eye, Save, Trash2, Users, Building2, UserCheck, Image as ImageIcon, Link, Bold, Italic, List, AlignLeft, AlignCenter, History, TestTube } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const NewsletterBuilder = () => {
  const [templates, setTemplates] = useState([]);
  const [recipientCounts, setRecipientCounts] = useState({ subscribers: 0, customers: 0, vendors: 0, all_unique: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('editor');
  
  // Editor state
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [content, setContent] = useState('');
  const editorRef = useRef(null);
  
  // Send state
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendRecipientType, setSendRecipientType] = useState('subscribers');
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  
  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  
  // History state
  const [sendHistory, setSendHistory] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templatesRes, countsRes, historyRes] = await Promise.all([
        axiosInstance.get('/newsletter/templates').catch(() => ({ data: [] })),
        axiosInstance.get('/newsletter/recipients').catch(() => ({ data: { subscribers: 0, customers: 0, vendors: 0, all_unique: 0 } })),
        axiosInstance.get('/newsletter/history').catch(() => ({ data: [] }))
      ]);
      setTemplates(templatesRes.data || []);
      setRecipientCounts(countsRes.data || { subscribers: 0, customers: 0, vendors: 0, all_unique: 0 });
      setSendHistory(historyRes.data || []);
    } catch (error) {
      console.error('Error loading newsletter data:', error);
      // Set defaults on error
      setTemplates([]);
      setRecipientCounts({ subscribers: 0, customers: 0, vendors: 0, all_unique: 0 });
      setSendHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!title || !subject || !content) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    try {
      if (editingTemplate) {
        await axiosInstance.put(`/newsletter/templates/${editingTemplate.id}`, {
          title, subject, content, preview_text: previewText
        });
        toast.success('Newsletter aktualisiert!');
      } else {
        await axiosInstance.post('/newsletter/templates', {
          title, subject, content, preview_text: previewText
        });
        toast.success('Newsletter gespeichert!');
      }
      resetEditor();
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Newsletter wirklich löschen?')) return;
    try {
      await axiosInstance.delete(`/newsletter/templates/${templateId}`);
      toast.success('Newsletter gelöscht');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleSend = async (isTest = false) => {
    if (!editingTemplate) {
      toast.error('Bitte wählen Sie einen Newsletter aus');
      return;
    }

    setSending(true);
    try {
      const payload = {
        newsletter_id: editingTemplate.id,
        recipient_type: sendRecipientType
      };
      
      if (isTest) {
        if (!testEmail) {
          toast.error('Bitte geben Sie eine Test-E-Mail ein');
          setSending(false);
          return;
        }
        payload.test_email = testEmail;
      }

      const response = await axiosInstance.post('/newsletter/send', payload);
      
      if (isTest) {
        toast.success(`Test-E-Mail an ${testEmail} gesendet!`);
      } else {
        toast.success(response.data.message);
        setShowSendDialog(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  const loadTemplate = (template) => {
    setEditingTemplate(template);
    setTitle(template.title);
    setSubject(template.subject);
    setPreviewText(template.preview_text || '');
    setContent(template.content);
    setActiveTab('editor');
  };

  const resetEditor = () => {
    setEditingTemplate(null);
    setTitle('');
    setSubject('');
    setPreviewText('');
    setContent('');
  };

  const insertAtCursor = (tag) => {
    const textarea = editorRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    
    let newText = '';
    switch (tag) {
      case 'bold':
        newText = `<strong>${selectedText || 'Text'}</strong>`;
        break;
      case 'italic':
        newText = `<em>${selectedText || 'Text'}</em>`;
        break;
      case 'link':
        newText = `<a href="URL">${selectedText || 'Link-Text'}</a>`;
        break;
      case 'image':
        newText = `<img src="BILD-URL" alt="Beschreibung" style="max-width:100%;" />`;
        break;
      case 'list':
        newText = `<ul>\n  <li>Punkt 1</li>\n  <li>Punkt 2</li>\n</ul>`;
        break;
      case 'center':
        newText = `<div style="text-align:center;">${selectedText || 'Text'}</div>`;
        break;
      case 'button':
        newText = `<a href="URL" style="display:inline-block;padding:12px 24px;background:#5D4037;color:white;text-decoration:none;">Button-Text</a>`;
        break;
      default:
        newText = tag;
    }
    
    const newContent = content.substring(0, start) + newText + content.substring(end);
    setContent(newContent);
  };

  const defaultTemplate = `
<div style="max-width:600px;margin:0 auto;font-family:Arial,sans-serif;">
  <div style="background:#5D4037;padding:20px;text-align:center;">
    <h1 style="color:white;margin:0;">Homeyoursoul</h1>
  </div>
  
  <div style="padding:30px;background:#ffffff;">
    <h2 style="color:#333;">Hallo!</h2>
    <p style="color:#666;line-height:1.6;">
      Hier kommt Ihr Newsletter-Text...
    </p>
    
    <div style="text-align:center;margin:30px 0;">
      <a href="https://homeyoursoul.de" style="display:inline-block;padding:12px 24px;background:#5D4037;color:white;text-decoration:none;">
        Zum Shop
      </a>
    </div>
  </div>
  
  <div style="background:#f5f5f5;padding:20px;text-align:center;font-size:12px;color:#999;">
    <p>© 2026 Homeyoursoul</p>
    <p><a href="{unsubscribe_url}" style="color:#999;">Newsletter abbestellen</a></p>
  </div>
</div>`.trim();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-[#8B7355]">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="p-4" style={{ backgroundColor: '#F5EDE4', minHeight: '100vh' }}>
      <div className="max-w-7xl mx-auto">
        {/* Goldbraune Haupt-Überschrift */}
        <div className="mb-4 p-3 md:p-4 flex items-center justify-between" style={{ backgroundColor: '#C4A084' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded flex items-center justify-center bg-white/90">
              <Mail className="h-5 w-5" style={{ color: '#C4A084' }} />
            </div>
            <div>
              <h1 className="text-lg md:text-xl font-bold text-white">Newsletter Builder</h1>
              <p className="text-xs md:text-sm text-white/80">Erstellen und versenden Sie Newsletter</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={resetEditor}
            className="rounded-none bg-white text-[#C4A084] hover:bg-white/90 border-0"
          >
            <Plus className="h-4 w-4 mr-2" />
            Neu
          </Button>
        </div>

        {/* Section: Recipient Stats - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0F8FF]">
            <Users className="h-4 w-4 text-[#60A0D0]" />
          </div>
          <h2 className="text-base font-semibold text-white">Empfänger-Übersicht</h2>
        </div>

        {/* Recipient Stats - Very light pastel colors */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <Card className="rounded-none bg-emerald-50/70 border-emerald-100">
            <CardContent className="p-3 flex items-center gap-3">
              <UserCheck className="h-5 w-5 text-emerald-500" />
              <div>
                <p className="text-xs text-gray-600">Newsletter-Abonnenten</p>
                <p className="text-lg font-bold text-gray-800">{recipientCounts.subscribers}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-none bg-sky-50/70 border-sky-100">
            <CardContent className="p-3 flex items-center gap-3">
              <Users className="h-5 w-5 text-sky-500" />
              <div>
                <p className="text-xs text-gray-600">Kunden</p>
                <p className="text-lg font-bold text-gray-800">{recipientCounts.customers}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-none bg-violet-50/70 border-violet-100">
            <CardContent className="p-3 flex items-center gap-3">
              <Building2 className="h-5 w-5 text-violet-500" />
              <div>
                <p className="text-xs text-gray-600">Verkäufer</p>
                <p className="text-lg font-bold text-gray-800">{recipientCounts.vendors}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-none bg-amber-50/70 border-amber-100">
            <CardContent className="p-3 flex items-center gap-3">
              <Mail className="h-5 w-5 text-amber-500" />
              <div>
                <p className="text-xs text-gray-600">Alle (unique)</p>
                <p className="text-lg font-bold">{recipientCounts.all_unique}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Section: Editor */}
        <h2 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Mail className="h-4 w-4 text-[#8B7355]" />
          Newsletter-Editor
        </h2>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#C4B5A4] p-1 rounded-none border-0 gap-1 mb-4 inline-flex">
            <TabsTrigger value="editor" className="rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-4 py-2">Editor</TabsTrigger>
            <TabsTrigger value="templates" className="rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-4 py-2">Gespeicherte ({templates.length})</TabsTrigger>
            <TabsTrigger value="history" className="rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-4 py-2">Verlauf</TabsTrigger>
          </TabsList>

          <TabsContent value="editor">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Editor */}
              <Card className="rounded-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">
                    {editingTemplate ? `Bearbeiten: ${editingTemplate.title}` : 'Neuer Newsletter'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Interner Titel *</label>
                    <Input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="z.B. Oster-Newsletter 2026"
                      className="rounded-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Betreff *</label>
                    <Input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="z.B. 🐣 Frohe Ostern! 20% Rabatt auf alles"
                      className="rounded-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Vorschau-Text</label>
                    <Input
                      value={previewText}
                      onChange={(e) => setPreviewText(e.target.value)}
                      placeholder="Kurzer Text der im Posteingang angezeigt wird"
                      className="rounded-none"
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium">Inhalt (HTML) *</label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setContent(defaultTemplate)}
                        className="text-xs"
                      >
                        Vorlage laden
                      </Button>
                    </div>
                    
                    {/* Toolbar */}
                    <div className="flex flex-wrap gap-1 mb-2 p-2 bg-gray-100 border">
                      <Button variant="ghost" size="sm" onClick={() => insertAtCursor('bold')} title="Fett">
                        <Bold className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => insertAtCursor('italic')} title="Kursiv">
                        <Italic className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => insertAtCursor('link')} title="Link">
                        <Link className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => insertAtCursor('image')} title="Bild">
                        <ImageIcon className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => insertAtCursor('list')} title="Liste">
                        <List className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => insertAtCursor('center')} title="Zentrieren">
                        <AlignCenter className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => insertAtCursor('button')} title="Button">
                        Button
                      </Button>
                    </div>
                    
                    <textarea
                      ref={editorRef}
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      className="w-full h-64 p-3 border font-mono text-sm"
                      placeholder="HTML-Inhalt hier eingeben..."
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button onClick={handleSave} className="rounded-none flex-1 bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0">
                      <Save className="h-4 w-4 mr-2" />
                      Speichern
                    </Button>
                    <Button 
                      onClick={() => setShowPreview(true)}
                      className="rounded-none bg-[#E3F2FD] text-[#2196F3] hover:bg-[#BBDEFB] border-0"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Vorschau
                    </Button>
                    {editingTemplate && (
                      <Button 
                        onClick={() => setShowSendDialog(true)}
                        className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Senden
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Live Preview */}
              <Card className="rounded-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Live-Vorschau</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border bg-white p-4 min-h-[400px] overflow-auto">
                    {content ? (
                      <div dangerouslySetInnerHTML={{ __html: content }} />
                    ) : (
                      <p className="text-gray-400 text-center py-20">
                        Inhalt eingeben um Vorschau zu sehen...
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="templates">
            <Card className="rounded-none">
              <CardContent className="p-4">
                {templates.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">Noch keine Newsletter gespeichert</p>
                ) : (
                  <div className="space-y-2">
                    {templates.map((template) => (
                      <div 
                        key={template.id}
                        className="flex items-center justify-between gap-2 p-3 sm:p-4 border bg-white hover:bg-gray-50 overflow-hidden"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{template.title}</p>
                          <p className="text-sm text-gray-600 truncate">{template.subject}</p>
                          <p className="text-xs text-gray-400 mt-1 truncate">
                            Erstellt: {new Date(template.created_at).toLocaleDateString('de-DE')}
                            {template.send_count > 0 && ` | ${template.send_count}x gesendet`}
                          </p>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => loadTemplate(template)}
                            className="rounded-none whitespace-nowrap"
                          >
                            <span className="hidden sm:inline">Bearbeiten</span>
                            <span className="sm:hidden">Bearb.</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(template.id)}
                            className="rounded-none text-red-600 h-9 w-9 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card className="rounded-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Versand-Verlauf
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sendHistory.length === 0 ? (
                  <p className="text-gray-600 text-center py-8">Noch keine Newsletter versendet</p>
                ) : (
                  <div className="space-y-2">
                    {sendHistory.map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between p-3 border bg-white">
                        <div>
                          <p className="font-medium">
                            {entry.is_test ? '🧪 Test-Versand' : `📧 ${entry.recipient_type}`}
                          </p>
                          <p className="text-sm text-gray-600">
                            {entry.sent_count} gesendet, {entry.failed_count} fehlgeschlagen
                          </p>
                        </div>
                        <p className="text-sm text-gray-400">
                          {new Date(entry.sent_at).toLocaleString('de-DE')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Send Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Newsletter versenden</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Empfänger auswählen</label>
              <div className="space-y-2">
                {[
                  { value: 'subscribers', label: 'Newsletter-Abonnenten', count: recipientCounts.subscribers, icon: UserCheck },
                  { value: 'customers', label: 'Alle Kunden', count: recipientCounts.customers, icon: Users },
                  { value: 'vendors', label: 'Alle Verkäufer', count: recipientCounts.vendors, icon: Building2 },
                  { value: 'all', label: 'Alle (unique)', count: recipientCounts.all_unique, icon: Mail }
                ].map(({ value, label, count, icon: Icon }) => (
                  <label 
                    key={value}
                    className={`flex items-center gap-3 p-3 border cursor-pointer ${
                      sendRecipientType === value ? 'border-amber-500 bg-amber-50' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="recipient"
                      value={value}
                      checked={sendRecipientType === value}
                      onChange={(e) => setSendRecipientType(e.target.value)}
                    />
                    <Icon className="h-4 w-4" />
                    <span className="flex-1">{label}</span>
                    <span className="text-sm text-gray-500">{count}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="border-t pt-4">
              <label className="block text-sm font-medium mb-2">
                <TestTube className="h-4 w-4 inline mr-1" />
                Test-Versand
              </label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  className="rounded-none flex-1"
                />
                <Button
                  variant="outline"
                  onClick={() => handleSend(true)}
                  disabled={sending}
                  className="rounded-none"
                >
                  Test senden
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)} className="rounded-none">
              Abbrechen
            </Button>
            <Button 
              onClick={() => handleSend(false)}
              disabled={sending}
              className="rounded-none bg-green-600 hover:bg-green-700"
            >
              {sending ? 'Sendet...' : `An ${recipientCounts[sendRecipientType] || recipientCounts.all_unique} Empfänger senden`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl rounded-none">
          <DialogHeader>
            <DialogTitle>Newsletter-Vorschau</DialogTitle>
          </DialogHeader>
          <div className="border bg-white p-4 max-h-[70vh] overflow-auto">
            <div dangerouslySetInnerHTML={{ __html: content }} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsletterBuilder;
